import React, { FC } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone';
import {
  Box,
  Text,
  Accordion,
  accordionItemBuilder,
  TextButton,
} from '@wix/design-system';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import {
  ChevronDown,
  ChevronUp,
  CircleSmallFilledSmall,
} from '@wix/wix-ui-icons-common';
import FilterPanelHeader from './FilterPanelHeader';
import SiteTypeFilter from './SiteTypeFilter';
import IndustriesFilter from './IndustriesFilter';
import FeaturesFilter from './FeaturesFilter';
import LanguageFilter from './LanguageFilter';
import StylesFilter from './StylesFilter';
import PriceFilter from './PriceFilter';
import { useTemplateGalleryFilters } from '@components/Hooks/useTemplateGalleryFilters/useTemplateGalleryFilters';
import s from './FilterPanel.module.scss';
import { FILTER_TO_TITLE_MAP } from '../TemplateMobileFilters/TemplatesMobileFiltersMenu/TemplatesMobileFiltersMenu';
import { Filters as IFilters } from '@common/types';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import LanguagesFilter from './LanguagesFilter';

type Props = {};

const FilterPanel: FC<Props> = ({}) => {
  const {
    state: { filterOpen },
  } = useTemplatesContext();

  return (
    <Box
      className={`${s.leftBarWrapper} ${filterOpen ? s.filterOpen : ''}`}
      width={filterOpen ? '260px' : '24px'}
      direction="vertical"
      dataHook="templates-filter-panel"
      height="calc(100vh - 70px)"
    >
      <FilterPanelHeader />
      <Box
        className={s.leftBar}
        width="100%"
        minHeight="calc(100vh - 204px)"
        maxHeight="calc(100vh - 48px)"
      >
        <div className={s.filterContent}>
          <Box direction="vertical">
            <Filters />
          </Box>
        </div>
      </Box>
    </Box>
  );
};

const FILTER_TO_COMPONENT_MAP: Record<keyof IFilters, React.FC> = {
  siteType: SiteTypeFilter,
  industries: IndustriesFilter,
  features: FeaturesFilter,
  language: LanguageFilter,
  price: PriceFilter,
  styles: StylesFilter,
  languages: LanguagesFilter,
};

const BI_EVENT_CLICK_TYPE_MAP: Record<keyof IFilters, string> = {
  siteType: 'Site type',
  industries: 'Industry',
  features: 'Features',
  language: 'Language',
  price: 'Price',
  styles: 'style',
  languages: 'Languages',
};

const Filters: FC = () => {
  const { activeFilterMap, orderedFilterTypes } = useTemplateGalleryFilters();
  const { logBiClick } = useTemplatesBi({ is_mobile: false });
  const { t } = useTranslation();
  const accordionItems = orderedFilterTypes.map((filterType) => {
    const isActive = activeFilterMap[filterType];
    const ContentComponent = FILTER_TO_COMPONENT_MAP[filterType];
    return accordionItemBuilder({
      hideShadow: true,
      initiallyOpen: filterType === 'siteType' || isActive,
      horizontalPadding: 'large',
      title: (
        <FilterTitleWithIndicator
          isActive={isActive}
          i18nKey={FILTER_TO_TITLE_MAP[filterType]}
        />
      ),
      buttonType: 'node',
      showLabel: 'always',
      expandLabel: (
        <TextButton
          color="#3899EC"
          onClick={() => {
            logBiClick({
              click_type: `${BI_EVENT_CLICK_TYPE_MAP[filterType]} filter - open`,
            });
          }}
        >
          <ChevronUp
            aria-label={t('marketplace.templates-page.open-filter-aria-label')}
          />
        </TextButton>
      ),
      collapseLabel: (
        <TextButton
          color="#3899EC"
          onClick={() => {
            logBiClick({
              click_type: `${BI_EVENT_CLICK_TYPE_MAP[filterType]} filter - close`,
            });
          }}
        >
          <ChevronDown
            aria-label={t('marketplace.templates-page.close-filter-aria-label')}
          />
        </TextButton>
      ),
      children: <ContentComponent />,
    });
  });

  return (
    <Accordion
      dataHook="templates-filter-accordion"
      hideShadow
      horizontalPadding="large"
      multiple
      size="tiny"
      skin="light"
      items={accordionItems}
    />
  );
};

function FilterTitleWithIndicator({
  i18nKey,
  isActive,
}: {
  i18nKey: string;
  isActive: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box verticalAlign="middle">
      <Text weight="normal" size="small">
        {t(i18nKey)}
      </Text>
      {isActive && (
        <CircleSmallFilledSmall color="var(--wds-color-fill-standard-primary, var(--wsr-color-B10, #3899EC))" />
      )}
    </Box>
  );
}

export default FilterPanel;
